import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Scale, CheckCircle, Clock, XCircle, Eye, ChevronLeft, ChevronRight,
  User, DollarSign, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    loadDisputes();
  }, [statusFilter]);

  const loadDisputes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/disputes?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisputes(res.data.disputes || []);
    } catch (error) {
      console.error('Failed to load disputes:', error);
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  const resolveDispute = async (disputeId, resolution) => {
    try {
      setResolving(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/disputes/${disputeId}/resolve`,
        { resolution, notes: resolutionNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dispute ${resolution}`);
      setSelectedDispute(null);
      setResolutionNotes('');
      loadDisputes();
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'open': { label: 'Open', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'pending': { label: 'Pending', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
      'resolved': { label: 'Resolved', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      'refunded': { label: 'Refunded', color: 'bg-[#00F0FF]/20 text-[#00F0FF]', icon: DollarSign },
      'denied': { label: 'Denied', color: 'bg-red-500/20 text-red-400', icon: XCircle },
    };
    return badges[status] || { label: status, color: 'bg-neutral-500/20 text-neutral-400', icon: Clock };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Scale className="w-8 h-8 text-blue-500" />
              Disputes
            </h1>
            <p className="text-neutral-400 mt-1">Review and resolve customer disputes</p>
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          {['all', 'open', 'pending', 'resolved', 'refunded', 'denied'].map(status => (
            <Button
              key={status}
              size="sm"
              variant={statusFilter === status ? 'default' : 'outline'}
              onClick={() => setStatusFilter(status)}
              className={statusFilter === status ? 'bg-[#00F0FF] text-black' : 'border-white/20'}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-12 text-neutral-400">
            <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No disputes found</p>
          </div>
        ) : (
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-black/30">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-neutral-400">Order</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-400">Buyer</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-400">Amount</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-400">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-400">Created</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {disputes.map((dispute) => {
                    const statusBadge = getStatusBadge(dispute.status);
                    const StatusIcon = statusBadge.icon;
                    return (
                      <tr key={dispute.dispute_id} className="hover:bg-white/5">
                        <td className="p-4">
                          <div className="font-mono text-sm">{dispute.order_id?.slice(0, 16)}...</div>
                          <div className="text-xs text-neutral-400">{dispute.order_items || 0} items</div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">{dispute.buyer_email || dispute.buyer_id}</div>
                          <div className="text-xs text-neutral-400">{dispute.buyer_name}</div>
                        </td>
                        <td className="p-4 font-semibold">
                          {formatCurrency(dispute.order_total || dispute.amount)}
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${statusBadge.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-neutral-400">
                          {formatDate(dispute.created_at)}
                        </td>
                        <td className="p-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedDispute(dispute)}
                            className="border-white/20"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {dispute.status === 'open' || dispute.status === 'pending' ? 'Resolve' : 'View'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Dispute Detail/Resolve Modal */}
        <Dialog open={!!selectedDispute} onOpenChange={() => { setSelectedDispute(null); setResolutionNotes(''); }}>
          <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-blue-500" />
                Dispute Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedDispute && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400">Dispute ID</div>
                    <div className="font-mono text-xs">{selectedDispute.dispute_id}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Order ID</div>
                    <div className="font-mono text-xs">{selectedDispute.order_id}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Amount</div>
                    <div className="font-semibold">{formatCurrency(selectedDispute.order_total || selectedDispute.amount)}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Status</div>
                    <div className="capitalize">{selectedDispute.status}</div>
                  </div>
                </div>

                <div>
                  <div className="text-neutral-400 text-sm mb-1">Buyer</div>
                  <div className="bg-black/30 p-2 rounded text-sm">
                    {selectedDispute.buyer_email || selectedDispute.buyer_id}
                    {selectedDispute.buyer_name && ` (${selectedDispute.buyer_name})`}
                  </div>
                </div>

                {selectedDispute.reason && (
                  <div>
                    <div className="text-neutral-400 text-sm mb-1">Reason</div>
                    <div className="bg-black/30 p-2 rounded text-sm">{selectedDispute.reason}</div>
                  </div>
                )}

                {(selectedDispute.status === 'open' || selectedDispute.status === 'pending') && (
                  <>
                    <div>
                      <div className="text-neutral-400 text-sm mb-1">Resolution Notes</div>
                      <Textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        placeholder="Add notes about this resolution..."
                        className="bg-black/30 border-white/10"
                      />
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-white/10">
                      <Button
                        onClick={() => resolveDispute(selectedDispute.dispute_id, 'resolved')}
                        disabled={resolving}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve
                      </Button>
                      <Button
                        onClick={() => resolveDispute(selectedDispute.dispute_id, 'refunded')}
                        disabled={resolving}
                        className="flex-1 bg-[#00D4E0] hover:bg-[#00B8C4]"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        Refund
                      </Button>
                      <Button
                        onClick={() => resolveDispute(selectedDispute.dispute_id, 'denied')}
                        disabled={resolving}
                        variant="outline"
                        className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Deny
                      </Button>
                    </div>
                  </>
                )}

                {selectedDispute.resolved_at && (
                  <div className="pt-4 border-t border-white/10 text-sm">
                    <div className="text-neutral-400">Resolved at: {formatDate(selectedDispute.resolved_at)}</div>
                    {selectedDispute.resolution_notes && (
                      <div className="mt-2 bg-black/30 p-2 rounded">
                        {selectedDispute.resolution_notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminDisputes;
